// TRADES — batch 2's rebuild of TradesLog (UI rebuild; drawn when lib/ui-v2.ts switches batch 2 on).
//
// KEEP THE BEHAVIOUR, CHANGE THE LAYOUT. The read is TradesLog's (GET /api/trades, the newest 200, the same query key);
// the filters are its filters (period, type, asset, status — Carried in hidden unless asked for — and the search over the
// shown name, the stored name, the slug, the merchant and the deal id); the ?q= and ?party= doors start the search box
// as before, and ?q= opens on All time; the CSV is its CSV. What changed is the drawing (Astra, trades):
//   · PageHead "Trades", Book trade the one gold primary;
//   · one search box, a compact date control beside it, the rest (type, asset, status, order) behind Filters;
//   · ONE register, newest first — a deal is one row (party and reference, when, the customer's two legs, the rate, the
//     state), in runs by the traded asset whose head says its bought · sold · deals (restored 1 Nov 2026, his "restore most
//     the lost things … with the improved UI"); the heads sort (party, the two legs); the foot carries the volume;
//   · a row opens the deal in the kit's DetailSheet (components/v2/trades/TradeDetail.tsx), its id in the URL (?deal=),
//     so Back closes it and the list underneath keeps its search.
// THE READS (design system Appendix B, through readState): a first read waits (SkeletonRows), a failed first read is a
// Notice with Try again, a failed refresh keeps its rows under one Notice and claims nothing from them (no count, no
// totals, no "nothing here"); an empty claim needs a successful read.
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Plus, SlidersHorizontal } from 'lucide-react';
import { api, type TradeRow } from '../../lib/api';
import { readState } from '../../lib/read-state';
import { displayAsset, dubaiDate, fmtAmount, fmtRate, fmtStamp, fmtTime } from '../../lib/format';
import { orient } from '../../lib/trade-math';
import { isFiat } from '../../lib/workflow';
import { partyName, shortReference } from '../../lib/naming';
import { Chip } from '../../components/FilterRail';
import { FilterSelect } from '../../components/FilterBar';
import { periodDays, type PeriodValue } from '../../components/PeriodChips';
import { Pill } from '../../components/Pill';
import { LAST_READ_NONE, ReadFailed } from '../../components/OnboardingParts';
import { Amount, rateQuote } from '../../components/Ledger';
import {
  tradeKind, KIND_LABEL, lifecycleLabel, lifecycleTone, operatorLabel, viaWords, type TradeKind,
} from '../../components/DealSlideOver';
import { rowLifecycle } from '../TradesLog';
import { EmptyState, Notice, TAP, PageHead, Panel, Register, SearchBar, SkeletonRows, Pending, type RegisterColumn, type RegisterGroup, ReadNotice, useSortParam, whenColumn } from '../../components/v2/kit';
import { PeriodDates, PeriodSelect } from '../../components/v2/trades/PeriodSelect';
import { TradeDetail } from '../../components/v2/trades/TradeDetail';

type KindFilter = 'all' | TradeKind;
type Order = 'new' | 'old' | 'party' | 'rate';

const KINDS: Array<[KindFilter, string]> = [['all', 'All'], ['buy', 'Buy'], ['sell', 'Sell'], ['mc', 'MC']];
const ORDERS: Array<{ value: Order; label: string }> = [
  { value: 'new', label: 'Newest first' }, { value: 'old', label: 'Oldest first' }, { value: 'party', label: 'Party A–Z' }, { value: 'rate', label: 'Rate' },
];

/** Is this trade's Dubai day inside the period? (TradesLog's rule: periodDays and dubaiDate, nothing else.) */
function inPeriod(iso: string, days: { from?: string; to?: string }): boolean {
  const d = dubaiDate(iso);
  return (!days.from || d >= days.from) && (!days.to || d <= days.to);
}

const tradeParty = (t: TradeRow): string => partyName({ display_name: t.party_name, slug: t.party_slug });
const tradeMerchant = (t: TradeRow): string =>
  !t.merchant_name && !t.merchant_slug ? '' : partyName({ display_name: t.merchant_name, slug: t.merchant_slug });

// ── CSV — TradesLog's export, unchanged: the STORED names, Dubai's day, the customer's two legs ─────────────────────
function csvEscape(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function exportCsv(rows: TradeRow[]): void {
  const header = [
    'Booked at (Dubai)', 'Deal id', 'Party', 'Type', 'Status',
    'Customer pays', 'Pays asset', 'Customer receives', 'Receives asset',
    'Rate', 'Spread', 'Via', 'Merchant', 'Booked by', 'Booked at (UTC)',
  ];
  const lines = rows.map((t) => [
    `${dubaiDate(t.booked_at)} ${fmtTime(t.booked_at)}`,
    t.deal_id, t.party_name, KIND_LABEL[tradeKind(t)], lifecycleLabel(rowLifecycle(t)),
    t.amount_take, displayAsset(t.asset_take),
    t.amount_give, displayAsset(t.asset_give),
    t.rate ?? '', t.spread_amount ?? '',
    viaWords(t) ?? '',
    t.merchant_name ?? '', operatorLabel(t.booked_by_admin),
    t.booked_at,
  ].map(csvEscape).join(','));
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trades-${dubaiDate()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── the row's pieces ─────────────────────────────────────────────────────────────────────────────────────────────
/** One leg in its cell. A cheque's legs carry their word ("deposit", "cheque") — a cheque is not a trade at rate 1. */
function LegCell({ amount, asset, word }: { amount: string | number; asset: string; word?: string }) {
  return (
    <span className="inline-flex flex-wrap items-baseline justify-end gap-x-2">
      {word && <span className="text-meta text-tg-hint">{word}</span>}
      <Amount value={amount} asset={asset} />
    </span>
  );
}

/** THE STATE OF EVERY ROW, as a word on its pill (Astra: settled rows say Settled too). Two guards: a row whose
 *  response carries no lifecycle (an older Worker) is not called Settled from that silence, and over a failed read the
 *  cached rows say nothing is Settled — a claim needs a successful read. Every other state still shows. */
function StatePill({ t, known }: { t: TradeRow; known: boolean }) {
  const l = t.lifecycle || t.deal_id.startsWith('legacy2:') ? rowLifecycle(t) : null;
  if (!l || (l === 'settled' && !known)) return null;
  return <Pill tone={lifecycleTone(l)}>{lifecycleLabel(l)}</Pill>;
}

/** Reference · Dubai time — the second line under the party (Astra's mockup) — and the rate, which Astra's keep list
 *  keeps visible on the register (a cheque has none: it is not a trade at rate 1). */
/** The row's id under the party's name, ALONE (his "date and time everything in 1st column", 1 Nov 2026): the table has When
 *  and Rate columns; a card has its time gutter and a labelled Rate line under Pays / Receives. */
function RefLine({ t }: { t: TradeRow; card?: boolean }) {
  // The short reference ("TRD 0940B") — the full id cut to "TRD-2026-10-26…" at 1024 hid the part that tells two apart; the
  // whole id is its title and the opened deal's.
  return <span className="mt-1 block truncate font-mono text-meta font-normal text-tg-hint" title={t.deal_id}>{shortReference(t.deal_id)}</span>;
}

/** The phone's two legs, stacked as Astra's card (what the customer pays, then what they receive) — each with its term
 *  word, so the direction is never read from the order alone; the state pill sits on the first line, at the card's right
 *  edge (`w-screen max-w-full` makes the card's title take the row's whole width). */
function CardLegs({ t, known }: { t: TradeRow; known: boolean }) {
  const mc = tradeKind(t) === 'mc';
  return (
    <span className="mt-3 block w-screen max-w-full font-normal">
      <span className="flex items-center justify-between gap-3">
        <span className="min-w-0"><span className="text-meta text-tg-hint">{mc ? 'Deposit' : 'Pays'} </span><Amount value={t.amount_take} asset={t.asset_take} /></span>
        <span className="shrink-0"><StatePill t={t} known={known} /></span>
      </span>
      <span className="mt-1 block"><span className="text-meta text-tg-hint">{mc ? 'Cheque' : 'Receives'} </span><Amount value={t.amount_give} asset={t.asset_give} /></span>
      {!mc && <span className="mt-1 block"><span className="text-meta text-tg-hint">Rate </span><span className="font-mono tabular">{fmtRate(t.rate, rateQuote(t))}</span></span>}
    </span>
  );
}

const rateWords = (t: TradeRow): string => (tradeKind(t) === 'mc' ? '' : `@ ${fmtRate(t.rate, rateQuote(t))}`);

/** THE TRADED ASSET's runs (TradesLog's blocks): every deal under the higher-ranked of its two assets, and that asset's
 *  Customer buys (the customer received it) · Customer sells (the customer paid it) · deals, in the run's head. */
function tradeRuns(deals: readonly TradeRow[]): Array<RegisterGroup<TradeRow>> {
  const runs = new Map<string, { code: string; rows: TradeRow[]; bought: number; sold: number }>();
  for (const t of deals) {
    const code = orient(t.asset_take, t.asset_give).base;
    const label = displayAsset(code);
    const r = runs.get(label) ?? { code, rows: [], bought: 0, sold: 0 };
    r.rows.push(t);
    const give = Number(t.amount_give), take = Number(t.amount_take);
    if (displayAsset(t.asset_give) === label && Number.isFinite(give)) r.bought += give;
    else if (displayAsset(t.asset_take) === label && Number.isFinite(take)) r.sold += take;
    runs.set(label, r);
  }
  return [...runs.entries()]
    .sort((a, b) => b[1].rows.length - a[1].rows.length || a[0].localeCompare(b[0]))
    .map(([label, r]) => {
      // ON THE LAPTOP the totals sit under the columns they total (Astra's audit, 1 Nov 2026: a sentence of totals beside the head
      // read as one more packed line): what the customers paid in this asset under Customer pays, what they received under
      // Customer receives, the deal count under State. Figure then its muted unit, body size.
      const total = (word: string, n: number) => (
        <span className="block">
          <span className="block text-meta text-tg-hint">{word}</span>
          <span className="whitespace-nowrap"><span className="font-mono tabular text-tg-text">{fmtAmount(n, r.code)}</span> <span className="text-tg-hint">{label}</span></span>
        </span>
      );
      return {
        key: label,
        cells: {
          pays: r.sold > 0 ? total('Sells', r.sold) : null,
          gets: r.bought > 0 ? total('Buys', r.bought) : null,
          state: <span className="whitespace-nowrap text-meta text-tg-hint">{r.rows.length} {r.rows.length === 1 ? 'deal' : 'deals'}</span>,
        },
        head: label,
        // THE CARD LIST'S HEAD: labelled figures on its right — Buys / Sells / Deals, the label quiet, the figure mono, the unit
        // muted — never a sentence (the coordinator's audit, 1 Nov 2026).
        totals: (
          <>
            {r.bought > 0 && <span className="whitespace-nowrap"><span className="font-sans">Buys </span><span className="text-body text-tg-text">{fmtAmount(r.bought, r.code)}</span> <span className="font-sans">{label}</span></span>}
            {r.sold > 0 && <span className="whitespace-nowrap"><span className="font-sans">Sells </span><span className="text-body text-tg-text">{fmtAmount(r.sold, r.code)}</span> <span className="font-sans">{label}</span></span>}
            <span className="whitespace-nowrap"><span className="font-sans">Deals </span><span className="text-body text-tg-text">{r.rows.length}</span></span>
          </>
        ),
        rows: r.rows,
      };
    });
}

export const columns = (known: boolean): ReadonlyArray<RegisterColumn<TradeRow>> => [
  whenColumn<TradeRow>((t) => t.booked_at, { sortable: true }),
  {
    // The NAME takes what the fixed seats leave (two lines, the whole name its title); the figures never give way.
    key: 'party', head: 'Party / reference', sortBy: (t) => tradeParty(t),
    cell: (t) => (
      <span className="block min-w-0">
        <span className="line-clamp-2 break-words font-medium" title={tradeParty(t)}>{tradeParty(t)}</span>
        <RefLine t={t} />
      </span>
    ),
  },
  {
    key: 'pays', head: 'Customer pays', align: 'right', width: 'w-[9.75rem] xl:w-[11.5rem]', sortBy: (t) => Number(t.amount_take),
    cell: (t) => <LegCell amount={t.amount_take} asset={t.asset_take} word={tradeKind(t) === 'mc' ? 'deposit' : undefined} />,
  },
  {
    key: 'gets', head: 'Customer receives', align: 'right', width: 'w-[9.75rem] xl:w-[11.5rem]', sortBy: (t) => Number(t.amount_give),
    cell: (t) => <LegCell amount={t.amount_give} asset={t.asset_give} word={tradeKind(t) === 'mc' ? 'cheque' : undefined} />,
  },
  {
    key: 'rate', head: 'Rate', align: 'right', width: 'w-[4.5rem] xl:w-[7.5rem]', sortBy: (t) => (tradeKind(t) === 'mc' ? null : Number(t.rate)),
    cell: (t) => (tradeKind(t) === 'mc' ? <span className="text-tg-hint">—</span> : <span className="font-mono tabular">{fmtRate(t.rate, rateQuote(t))}</span>),
  },
  { key: 'state', head: 'State', align: 'right', width: 'w-[8rem] xl:w-[148px]', cell: (t) => <StatePill t={t} known={known} /> },
  { key: 'open', head: '', width: 'w-[1.75rem] xl:w-[44px]', cell: () => <ChevronRight className="ml-auto h-4 w-4 text-tg-hint" aria-hidden /> },
];

/** The phone's card (Astra): party, reference · time, the chevron; then the two legs with the state. */
const card = (known: boolean) => (t: TradeRow) => ({
  title: <>{tradeParty(t)}<RefLine t={t} card /><CardLegs t={t} known={known} /></>,
  figure: <ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden />,
});

export function TradesV2() {
  const q = useQuery({
    queryKey: ['trades', 'log', 'dubaiotc', 200],
    queryFn:  () => api.listTrades('dubaiotc', 200),
  });
  const rs = readState(q);

  // The doors: "All trades →" on a party page lands with ?party=<slug>, a statement row with ?q=<deal id>. Read once, as
  // the box's first value; a deal looked up by id is found whatever day it was booked, the bare screen opens on today.
  const [params, setParams] = useSearchParams();
  const [period, setPeriod] = useState<PeriodValue>(() => (params.get('q') ? { key: 'all' } : { key: 'today' }));
  const [kind, setKind]     = useState<KindFilter>('all');
  const [asset, setAsset]   = useState<string>('all');
  const [status, setStatus] = useState<string>('all');
  const [order, setOrder]   = useState<Order>('new');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState(() => params.get('q') ?? params.get('party') ?? '');

  const rows = q.data?.trades ?? [];
  const days = periodDays(period);

  const assetOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of rows) { set.add(displayAsset(t.asset_take)); set.add(displayAsset(t.asset_give)); }
    return [...set].sort();
  }, [rows]);
  // The route excludes voided deals, so this offers Settled, Carried in, Awaiting and the cheque states.
  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of rows) { const l = rowLifecycle(t); if (l) set.add(l); }
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((t) => {
      // CARRIED-IN ROWS ARE HIDDEN UNLESS ASKED FOR (§5.13): they sit inside the 1 Sept opening box.
      const lc = rowLifecycle(t);
      if (status === 'all' ? lc === 'absorbed' : lc !== status) return false;
      if (!inPeriod(t.booked_at, days)) return false;
      if (kind !== 'all' && tradeKind(t) !== kind) return false;
      if (asset !== 'all' && displayAsset(t.asset_take) !== asset && displayAsset(t.asset_give) !== asset) return false;
      if (needle) {
        // The displayed name and the stored one: "GRYPTO TG" still finds the row that now reads "Grypto".
        const hay = [tradeParty(t), t.party_name ?? '', t.party_slug, tradeMerchant(t), t.merchant_name ?? '', t.deal_id].join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, days.from, days.to, kind, asset, status, search]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    switch (order) {
      case 'old':   return Date.parse(a.booked_at) - Date.parse(b.booked_at);
      case 'party': return tradeParty(a).localeCompare(tradeParty(b));
      case 'rate':  return Number(a.rate ?? 0) - Number(b.rate ?? 0);
      default:      return Date.parse(b.booked_at) - Date.parse(a.booked_at);
    }
  }), [filtered, order]);

  const active = (kind !== 'all' ? 1 : 0) + (asset !== 'all' ? 1 : 0) + (status !== 'all' ? 1 : 0);
  const filtersSet = search !== '' || active > 0 || period.key !== 'today';
  const clearFilters = () => { setSearch(''); setKind('all'); setAsset('all'); setStatus('all'); setPeriod({ key: 'today' }); };

  // THE OPEN DEAL lives in the URL: Back closes it, and the list keeps its search underneath.
  const openId = params.get('deal');
  const selected = openId ? rows.find((t) => t.deal_id === openId) ?? null : null;
  const open = useCallback((t: TradeRow) => setParams((p) => { const n = new URLSearchParams(p); n.set('deal', t.deal_id); return n; }), [setParams]);
  const close = useCallback(() => setParams((p) => { const n = new URLSearchParams(p); n.delete('deal'); return n; }, { replace: true }), [setParams]);

  // THE RUNS and the volume (restored 1 Nov 2026): one run per traded asset, its bought · sold · deals in the head; the fiat
  // legs summed, the largest two, in the foot. Over a read that failed nothing is summed (the rows stay, uncounted).
  const runs = useMemo(() => tradeRuns(sorted), [sorted]);
  const volume = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const t of sorted) {
      const leg = isFiat(t.asset_take) ? { a: t.asset_take, n: Number(t.amount_take) } : isFiat(t.asset_give) ? { a: t.asset_give, n: Number(t.amount_give) } : null;
      if (leg && Number.isFinite(leg.n)) totals[leg.a] = (totals[leg.a] ?? 0) + leg.n;
    }
    return Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 2);
  }, [sorted]);
  const [sort, setSort] = useSortParam();

  // The row spec depends on whether the read is a read: over a failed one no row says Settled.
  const cols = useMemo(() => columns(rs === 'known'), [rs]);
  const cardOf = useMemo(() => card(rs === 'known'), [rs]);

  const head = (
    <PageHead
      title="Trades"
        inline
      sub="What was agreed"
      actions={
<Link to="/trade" className="btn-primary px-4 text-body"><Plus className="h-4 w-4" aria-hidden />Book trade</Link>
      }
    />
  );

  const filters = (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-label font-medium uppercase tracking-wider text-tg-hint">Type</div>
        <div className="flex flex-wrap gap-2">
          {KINDS.map(([k, label]) => <Chip key={k} active={kind === k} onClick={() => setKind(k)}>{label}</Chip>)}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <label className="block min-w-0">
          <span className="mb-2 block text-label font-medium uppercase tracking-wider text-tg-hint">Asset</span>
          <FilterSelect value={asset} onChange={setAsset} allValue="all" className="lg:w-full" options={assetOptions.map((a) => ({ value: a, label: a }))} />
        </label>
        {statusOptions.length > 0 && (
          <label className="block min-w-0">
            <span className="mb-2 block text-label font-medium uppercase tracking-wider text-tg-hint">Status</span>
            <FilterSelect value={status} onChange={setStatus} allValue="all" className="lg:w-full" options={statusOptions.map((s) => ({ value: s, label: lifecycleLabel(s) }))} />
          </label>
        )}
        <label className="block min-w-0">
          <span className="mb-2 block text-label font-medium uppercase tracking-wider text-tg-hint">Order</span>
          <FilterSelect value={order} onChange={setOrder} allValue="new" allLabel="Newest first" className="lg:w-full" options={ORDERS.filter((o) => o.value !== 'new')} />
        </label>
      </div>
      {filtersSet && <button type="button" className="btn-link text-body" onClick={clearFilters}>Clear filters</button>}
    </div>
  );

  // A first read on its way — the title stays, the rows say "reading", never "nothing here".
  if (rs === 'waiting' && !q.data) {
    return (
      <>
        {head}
        <Panel flush>
          <div className="px-4 pt-4 text-body lg:px-5"><Pending what="Reading the trades…" /></div>
          <SkeletonRows what="the trades" rows={5} />
        </Panel>
      </>
    );
  }
  // A first read that failed: one Notice and its Try again, nothing drawn under it.
  if (rs === 'failed' && !q.data) {
    return (
      <>
        {head}
        <ReadNotice what="the trades" error={q.error} onRetry={() => { void q.refetch(); }} />
      </>
    );
  }

  const known = rs === 'known';
  // THE LOG IS ONE PAGE (the newest 200, the route's cap): a full page is a cut read, and nothing claims "none" over it.
  const cut = rows.length >= 200;
  return (
    <>
      {head}
      {/* A failed refresh keeps the rows it had and says so, once; nothing below is counted from them. */}
      {rs === 'failed' && (
        <Notice tone="warn">
          <ReadFailed what="the trades" error={q.error} says={sorted.length === 0 ? LAST_READ_NONE : 'showing the last good read'} retry={() => { void q.refetch(); }} />
        </Notice>
      )}

      {/* Astra's bar: the search, then Filters and the date (side by side under the search on the phone). The filters
          panel is this screen's own, so the phone can seat the two buttons as Astra's mockup does. */}
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search name or reference"
        extra={
          <div className="flex w-full gap-2 sm:w-auto">
            <button type="button" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((v) => !v)} className="btn-secondary flex-1 px-3 text-body sm:flex-none">
              <SlidersHorizontal className="h-4 w-4" aria-hidden />Filters{active > 0 && <span className="font-mono tabular text-tg-accent">{active}</span>}
            </button>
            <PeriodSelect value={period} onChange={setPeriod} />
          </div>
        }
      />
      {filtersOpen && <div className="-mt-2 mb-4 rounded-[10px] border border-stroke bg-panel p-4">{filters}</div>}
      <PeriodDates value={period} onChange={setPeriod} />

      <Panel flush>
        {sorted.length > 0 && (
          <Register label="Trades" columns={cols} groups={known ? runs : undefined} rows={sorted} sort={sort} onSort={setSort} rowKey={(t) => t.deal_id} onOpen={open} card={cardOf} />
        )}
        {/* AN EMPTY CLAIM NEEDS A SUCCESSFUL READ. */}
        {sorted.length === 0 && known && (
          <EmptyState className="px-4 lg:py-16" action={filtersSet ? <button type="button" className="btn-link" onClick={clearFilters}>Clear filters</button> : undefined}>
            {cut ? 'None in the newest 200.' : period.key === 'all' ? 'Nothing here.' : 'Nothing in this period.'}
          </EmptyState>
        )}
        {/* ASTRA'S FOOT: "N records · Newest first" and "Dubai time". The count only from a successful read, and a full
            page (the route's 200) says it is the first 200, never all. Export CSV is the one door to the export: a quiet link. */}
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
          <span className="min-w-0">
            <span className="block whitespace-nowrap">
              {[
                known && sorted.length > 0 && `${cut ? 'First ' : ''}${sorted.length} ${sorted.length === 1 ? 'record' : 'records'}`,
                sorted.length > 0 && !sort && (ORDERS.find((o) => o.value === order)?.label ?? 'Newest first'),
              ].filter(Boolean).join(' · ')}
            </span>
            {/* The volume, its own line: the fiat legs summed, the largest two, amounts in the body role. */}
            {known && volume.length > 0 && (
              <span className="mt-1 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                <span>Volume</span>
                {volume.map(([a, n]) => (
                  <span key={a} className="whitespace-nowrap"><span className="font-mono tabular text-body font-medium text-tg-text">{fmtAmount(n, a)}</span> {a}</span>
                ))}
              </span>
            )}
          </span>
          {sorted.length > 0 && <button type="button" className="btn-secondary min-h-11 px-4 text-body lg:min-h-10" onClick={() => exportCsv(sorted)}>Export CSV</button>}
        </div>
      </Panel>

      {selected && <TradeDetail key={selected.deal_id} deal={selected} onClose={close} />}
    </>
  );
}
